import { CloseOutlined, FilePdfOutlined, InboxOutlined } from "@ant-design/icons";
import { Button, Col, Typography, Upload, UploadProps, message } from "antd";
import Dragger from "antd/es/upload/Dragger";
import styles from "./createnotemodal.module.scss";

const MAX_PDF_COUNT = 3;
const MAX_PDF_SIZE = 50 * 1024 * 1024; // 50MB

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const PDFContextUploader = ({
  contextPdfFiles,
  onContextPdfFilesChange,
}: {
  contextPdfFiles: File[];
  onContextPdfFilesChange: (files: File[]) => void;
}) => {
  const componentName = "createnotemodal";
  const uploadDisabled = contextPdfFiles.length >= MAX_PDF_COUNT;

  const handleBeforeUpload = (file: File, fileList: File[]) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      message.error(`Only PDF files are allowed. Invalid file: ${file.name}`);
      return Upload.LIST_IGNORE;
    }

    const isLastFile = fileList[fileList.length - 1] === file;
    if (!isLastFile) {
      return false;
    }

    const availableSlots = MAX_PDF_COUNT - contextPdfFiles.length;
    if (availableSlots <= 0) {
      message.error(`Maximum of ${MAX_PDF_COUNT} PDFs allowed.`);
      return false;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const f of fileList) {
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        invalidFiles.push(`${f.name}: Only PDF files allowed`);
        continue;
      }
      if (f.size > MAX_PDF_SIZE) {
        invalidFiles.push(`${f.name}: File too large (max 50MB)`);
        continue;
      }
      validFiles.push(f);
    }

    if (validFiles.length > availableSlots) {
      const extra = validFiles.length - availableSlots;
      validFiles.splice(availableSlots);
      invalidFiles.push(`${extra} file(s) skipped: Maximum ${MAX_PDF_COUNT} PDFs allowed`);
    }

    if (invalidFiles.length > 0) {
      message.error(
        <div>
          <div>Some files were skipped:</div>
          {invalidFiles.map((msg, i) => (
            <div key={i} style={{ fontSize: "12px" }}>
              • {msg}
            </div>
          ))}
        </div>,
        5,
      );
    }

    if (validFiles.length > 0) {
      onContextPdfFilesChange([...contextPdfFiles, ...validFiles]);
      message.success(`${validFiles.length} PDF${validFiles.length > 1 ? "s" : ""} ready to upload`);
    }

    return false;
  };

  const handleRemove = (index: number) => {
    onContextPdfFilesChange(contextPdfFiles.filter((_, i) => i !== index));
  };

  const pdfProps: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: true,
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    disabled: uploadDisabled,
  };

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`} style={{ marginTop: "12px" }}>
      <Typography.Text strong style={{ display: "block", marginBottom: "6px" }}>
        Additional Context PDFs <Typography.Text type="secondary">(optional)</Typography.Text>
      </Typography.Text>
      <Typography.Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: "10px" }}>
        Upload referral documents, prior assessments, or other PDFs to include as additional context in the note.
      </Typography.Text>

      {contextPdfFiles.length > 0 && (
        <div style={{ marginBottom: "10px" }}>
          {contextPdfFiles.map((file, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                background: "#f6ffed",
                borderRadius: "4px",
                marginBottom: "6px",
                border: "1px dashed #b7eb8f",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                <FilePdfOutlined style={{ fontSize: "18px", color: "#52c41a" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text ellipsis={{ tooltip: file.name }} style={{ display: "block", fontWeight: 500 }}>
                    {file.name}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: "11px" }}>
                    {formatFileSize(file.size)}
                  </Typography.Text>
                </div>
              </div>
              <Button
                type="text"
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => handleRemove(index)}
                style={{ marginLeft: "8px" }}
              />
            </div>
          ))}
        </div>
      )}

      <Dragger {...pdfProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {uploadDisabled ? `Maximum ${MAX_PDF_COUNT} PDFs reached` : "Click or drag PDF files here"}
        </p>
        <p className="ant-upload-hint">
          {uploadDisabled
            ? `Remove a PDF to upload another (${contextPdfFiles.length}/${MAX_PDF_COUNT})`
            : `Up to ${MAX_PDF_COUNT} PDFs, max 50MB each (${contextPdfFiles.length}/${MAX_PDF_COUNT})`}
        </p>
      </Dragger>
    </Col>
  );
};

export default PDFContextUploader;
